import { Alert, AlertIcon, Badge, Box, Button, Heading, HStack, Link, Stack, Text } from '@chakra-ui/react';
import { zWorkbookJobsResponse } from '@resistapp/common/api-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { getAuthHeaders, resistomapApiClient } from '../../../utils/fetch';

const statusLabels = {
  queued: 'Queued',
  running: 'Processing',
  succeeded: 'Completed',
  failed: 'Needs attention',
  cancelled: 'Cancelled',
};
const statusColors = { queued: 'blue', running: 'blue', succeeded: 'green', failed: 'red', cancelled: 'gray' };

export function ChipImportJobs({ workbookId }: { workbookId: string }) {
  const queryClient = useQueryClient();
  const jobs = useQuery({
    queryKey: ['workbookJobs', workbookId],
    queryFn: async () => {
      const response = await resistomapApiClient.get(`/lab/${workbookId}/jobs`, { headers: getAuthHeaders() });
      return zWorkbookJobsResponse.parse(response.data).jobs;
    },
    refetchInterval: 5000,
  });
  const finished =
    jobs.data
      ?.filter(job => job.status === 'succeeded')
      .map(job => job.id)
      .sort()
      .join(',') ?? '';
  const finishedRef = useRef(finished);
  useEffect(() => {
    if (finished !== finishedRef.current) {
      finishedRef.current = finished;
      void queryClient.invalidateQueries({ queryKey: ['labWorkbook', workbookId] });
    }
  }, [finished, queryClient, workbookId]);
  const action = useMutation({
    mutationFn: async ({ id, action: operation }: { id: string; action: 'retry' | 'cancel' }) => {
      await resistomapApiClient.post(`/lab/${workbookId}/jobs/${id}/${operation}`, undefined, {
        headers: getAuthHeaders(),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['workbookJobs', workbookId] });
    },
  });
  if (jobs.isError)
    return (
      <Alert status="warning">
        <AlertIcon />
        Import status could not be loaded. Refresh before uploading the files again.
      </Alert>
    );
  if (!jobs.data?.length) return null;
  return (
    <Box borderWidth="1px" borderRadius="md" padding={4}>
      <Heading size="md" fontSize="lg" mb={3}>
        Chip imports
      </Heading>
      <Stack spacing={3}>
        {jobs.data.map(job => (
          <Box key={job.id} data-testid={`chip-import-${job.id}`}>
            <HStack justify="space-between" flexWrap="wrap">
              <HStack flexWrap="wrap" minWidth={0}>
                <Text fontWeight="medium" overflowWrap="anywhere">
                  {job.label}
                </Text>
                <Badge colorScheme={statusColors[job.status]}>{statusLabels[job.status]}</Badge>
                <Text
                  as="time"
                  dateTime={job.createdAt.toISOString()}
                  data-testid="chip-import-submitted-at"
                  fontSize="xs"
                  color="gray.600"
                  whiteSpace="nowrap"
                  width="15rem"
                  sx={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  Submitted {job.createdAt.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'medium' })}
                </Text>
              </HStack>
              <HStack>
                {job.result && (
                  <Link as={RouterLink} to={`/chip/${job.result.chipId}`} color="blue.600">
                    View chip
                  </Link>
                )}
                {job.canManage && job.canRetry && (job.status === 'failed' || job.status === 'cancelled') && (
                  <Button
                    size="xs"
                    isDisabled={action.isPending}
                    onClick={() => {
                      action.mutate({ id: job.id, action: 'retry' });
                    }}
                  >
                    Retry
                  </Button>
                )}
                {job.canManage && (job.status === 'queued' || job.status === 'running') && (
                  <Button
                    size="xs"
                    variant="ghost"
                    isDisabled={action.isPending}
                    onClick={() => {
                      action.mutate({ id: job.id, action: 'cancel' });
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </HStack>
            </HStack>
            {job.message && job.message !== statusLabels[job.status] && (
              <Text fontSize="sm" color={job.status === 'failed' ? 'red.600' : 'gray.600'}>
                {job.message}
              </Text>
            )}
          </Box>
        ))}
        {action.isError && (
          <Alert status="error">
            <AlertIcon />
            {action.error.message}
          </Alert>
        )}
      </Stack>
    </Box>
  );
}
